import {inject} from '@angular/core';
import {CanActivateChildFn, Router} from '@angular/router';
import {catchError, filter, map, of, switchMap, take} from 'rxjs';
import {CurrentUserContextService} from '../services/current-user-context.service';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {environment} from '../../../environments/environment';
import {UserRole} from '../../../models/generated-models';
import {STAFF_ROLES} from '../../app.constants';

/**
 * Forces a freshly-provisioned (PENDING) user to complete their personal
 * info in the onboarding wizard (role is always USER) before reaching any
 * other authenticated screen. Once the profile is saved the backend flips
 * the status to ACTIVE and {@link CurrentUserContextService.refresh} lets
 * navigation through again.
 */
export const profileCompletionGuard: CanActivateChildFn = (route, state) => {
  const currentUser = inject(CurrentUserContextService);
  const router = inject(Router);
  const oidc = inject(OidcSecurityService);

  // Always allow the onboarding wizard so a PENDING user can complete
  // their profile. (The unused personal/edit page was removed.)
  if (state.url.startsWith('/profile/onboarding')) {
    return true;
  }

  return oidc.userData$.pipe(
    filter(e => !!e?.userData),
    take(1),
    switchMap(u => {
      const roles = [
        ...(u.userData?.resource_access?.[environment.clientId]?.roles ?? []),
        ...(u.userData?.realm_access?.roles ?? []),
      ] as UserRole[];
      if (STAFF_ROLES.some(r => roles.includes(r))) {
        return of(true);
      }
      return currentUser.load().pipe(
        map((profile) => {
          if (profile.profileCompleted) {
            return true;
          }
          console.warn(`[profileCompletionGuard] Blocked access to "${state.url}" (profile not completed). Redirecting to /profile/onboarding`);
          return router.createUrlTree(['/profile/onboarding']);
        }),
        catchError(() => of(true)),
      );
    }),
  );
};
